import { create } from 'zustand';
import { useLocalAuthStore } from '@/stores/local-auth.store';
import { useToastStore } from '@/stores/toast.store';

/**
 * @fileoverview Session timeout store (Zustand)
 * @description
 * React/Zustand port of the Angular `SessionTimeoutService` (HIPAA 30-minute
 * inactivity logout). Monitors user activity (mousemove, keypress, click,
 * touchstart, scroll), shows a warning 2 minutes before timeout, and logs out
 * after the full 30 minutes. Signal names (`showWarning`, `timeRemaining`) and
 * method names (`startMonitoring`, `stopMonitoring`, `resetActivity`,
 * `extendSession`) are reproduced 1:1, as are the durations.
 *
 * Adaptation notes:
 * - Angular lazily injected `LocalAuthService` to break a circular dependency
 *   and called `signOut()` on timeout. Per the React conversion rules, the
 *   cross-store side effect is decoupled via a registered logout callback
 *   (`setLogoutHandler`). The default handler calls
 *   `useLocalAuthStore.getState().signOut()` to reproduce Angular behaviour,
 *   followed by the same expiry message (surfaced via the toast store).
 */

/** Session timeout configuration (matches Angular exactly). */
export const TIMEOUT_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
export const WARNING_BEFORE_TIMEOUT = 2 * 60 * 1000; // Warn 2 minutes before timeout

interface SessionTimeoutState {
  /** Whether the timeout-warning banner should be shown */
  showWarning: boolean;
  /** Seconds remaining until timeout */
  timeRemaining: number;

  startMonitoring: () => void;
  stopMonitoring: () => void;
  resetActivity: () => void;
  extendSession: () => void;
  /** Register the logout side effect (decouples cross-store DI). */
  setLogoutHandler: (handler: () => Promise<void> | void) => void;
}

let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
let warningTimer: ReturnType<typeof setTimeout> | undefined;
let activityCheckInterval: ReturnType<typeof setInterval> | undefined;
let lastActivityTime: number = Date.now();
let activityHandler: (() => void) | null = null;

/** Default logout handler: reproduces Angular handleTimeout's signOut + notice. */
let logoutHandler: () => Promise<void> | void = async () => {
  await useLocalAuthStore.getState().signOut();
  useToastStore
    .getState()
    .showToast('Your session has expired due to inactivity. Please log in again.', 'warning');
};

export const useSessionTimeoutStore = create<SessionTimeoutState>((set, get) => {
  const clearAllTimers = (): void => {
    if (timeoutTimer) {
      clearTimeout(timeoutTimer);
      timeoutTimer = undefined;
    }
    if (warningTimer) {
      clearTimeout(warningTimer);
      warningTimer = undefined;
    }
    if (activityCheckInterval) {
      clearInterval(activityCheckInterval);
      activityCheckInterval = undefined;
    }
  };

  const handleTimeout = async (): Promise<void> => {
    get().stopMonitoring();
    set({ showWarning: false });
    await logoutHandler();
  };

  const resetTimers = (): void => {
    if (timeoutTimer) clearTimeout(timeoutTimer);
    if (warningTimer) clearTimeout(warningTimer);

    // Warning timer (28 minutes)
    warningTimer = setTimeout(() => {
      set({ showWarning: true });
    }, TIMEOUT_DURATION - WARNING_BEFORE_TIMEOUT);

    // Timeout timer (30 minutes)
    timeoutTimer = setTimeout(() => {
      void handleTimeout();
    }, TIMEOUT_DURATION);
  };

  const startActivityCheck = (): void => {
    activityCheckInterval = setInterval(() => {
      const elapsed = Date.now() - lastActivityTime;
      const remaining = Math.max(0, TIMEOUT_DURATION - elapsed);
      set({ timeRemaining: Math.floor(remaining / 1000) });
      if (remaining === 0) {
        void handleTimeout();
      }
    }, 1000);
  };

  const handleActivity = (): void => {
    const now = Date.now();
    if (now - lastActivityTime > 1000) {
      get().resetActivity();
    }
  };

  const setupEventListeners = (): void => {
    activityHandler = handleActivity;
    document.addEventListener('mousemove', activityHandler, { passive: true });
    document.addEventListener('keypress', activityHandler, { passive: true });
    document.addEventListener('click', activityHandler, { passive: true });
    document.addEventListener('touchstart', activityHandler, { passive: true });
    document.addEventListener('scroll', activityHandler, { passive: true });
  };

  const removeEventListeners = (): void => {
    if (!activityHandler) return;
    document.removeEventListener('mousemove', activityHandler);
    document.removeEventListener('keypress', activityHandler);
    document.removeEventListener('click', activityHandler);
    document.removeEventListener('touchstart', activityHandler);
    document.removeEventListener('scroll', activityHandler);
    activityHandler = null;
  };

  return {
    showWarning: false,
    timeRemaining: 0,

    startMonitoring: () => {
      // Idempotency guard: drop any listeners from a prior start before
      // re-adding, so a double startMonitoring() can't leak duplicate document
      // event listeners. Timing/behavior is otherwise unchanged.
      removeEventListeners();
      resetTimers();
      setupEventListeners();
      startActivityCheck();
    },

    stopMonitoring: () => {
      clearAllTimers();
      removeEventListeners();
    },

    resetActivity: () => {
      lastActivityTime = Date.now();
      set({ showWarning: false });
      resetTimers();
    },

    extendSession: () => {
      get().resetActivity();
    },

    setLogoutHandler: (handler) => {
      logoutHandler = handler;
    },
  };
});
